<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div>
        <h2 class="text-lg font-bold text-neutral-900">下载与在线互动权限</h2>
        <p class="text-sm text-neutral-500 mt-1">
          两个开关互不干扰：可以只关下载保留在线互动，也可以只关在线互动保留下载（不影响浏览、搜索、收藏）
        </p>
      </div>

      <!-- 开关 1：下载 -->
      <div
        class="mt-6 rounded-xl border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-5"
        :class="downloadEnabled ? 'border-green-200 bg-green-50/60' : 'border-red-200 bg-red-50/60'"
      >
        <div class="flex items-start gap-4">
          <div
            class="w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
            :class="downloadEnabled ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'"
          >
            <i :class="'fa ' + (downloadEnabled ? 'fa-download' : 'fa-ban')"></i>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <span class="font-bold text-neutral-900">下载工具文件</span>
              <span
                class="text-xs px-2 py-0.5 rounded-full"
                :class="downloadEnabled ? 'bg-green-600 text-white' : 'bg-red-500 text-white'"
                >{{ downloadEnabled ? "已开启" : "已关闭" }}</span
              >
            </div>
            <p class="text-sm text-neutral-600 mt-1">
              {{
                downloadEnabled
                  ? "用户可以下载工具源文件到本地（仍按创值规则扣创值）。"
                  : "全站禁止下载：工具卡片与详情页的下载按钮会被禁用，后端也会直接拒绝下载请求。在线互动不受影响。"
              }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3 shrink-0">
          <span class="text-sm text-neutral-500">关闭</span>
          <el-switch
            v-model="downloadEnabled"
            :loading="saving"
            active-text="开启"
            style="--el-switch-on-color: #13ce66; --el-switch-off-color: #ff4949"
          />
          <span class="text-sm text-neutral-500">开启</span>
        </div>
      </div>

      <!-- 开关 2：在线互动 -->
      <div
        class="mt-4 rounded-xl border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-5"
        :class="playEnabled ? 'border-green-200 bg-green-50/60' : 'border-red-200 bg-red-50/60'"
      >
        <div class="flex items-start gap-4">
          <div
            class="w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
            :class="playEnabled ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'"
          >
            <i :class="'fa ' + (playEnabled ? 'fa-play-circle' : 'fa-ban')"></i>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <span class="font-bold text-neutral-900">在线互动使用</span>
              <span
                class="text-xs px-2 py-0.5 rounded-full"
                :class="playEnabled ? 'bg-green-600 text-white' : 'bg-red-500 text-white'"
                >{{ playEnabled ? "已开启" : "已关闭" }}</span
              >
            </div>
            <p class="text-sm text-neutral-600 mt-1">
              {{
                playEnabled
                  ? "用户可以在线打开并直接使用 HTML / ZIP 工具（仍按创值规则扣创值）。"
                  : "全站禁止在线互动：「在线互动」按钮会被禁用，在线使用页直接提示已关闭，后端也会拒绝。下载不受影响。"
              }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3 shrink-0">
          <span class="text-sm text-neutral-500">关闭</span>
          <el-switch
            v-model="playEnabled"
            :loading="saving"
            active-text="开启"
            style="--el-switch-on-color: #13ce66; --el-switch-off-color: #ff4949"
          />
          <span class="text-sm text-neutral-500">开启</span>
        </div>
      </div>

      <div class="mt-4 flex items-center gap-3">
        <button
          @click="save"
          :disabled="saving"
          class="px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          <i class="fa fa-save mr-1"></i>{{ saving ? "保存中…" : "保存设置" }}
        </button>
        <span v-if="tip" class="text-sm" :class="tipOk ? 'text-green-600' : 'text-red-500'">
          {{ tip }}
        </span>
      </div>

      <!-- 组合提示 -->
      <div
        class="mt-5 rounded-lg px-4 py-3 text-sm"
        :class="
          downloadEnabled && playEnabled
            ? 'bg-neutral-50 text-neutral-600'
            : 'bg-amber-50 text-amber-700'
        "
      >
        <i class="fa fa-info-circle mr-1"></i>
        <span v-if="downloadEnabled && playEnabled">当前：下载与在线互动均正常开放。</span>
        <span v-else-if="!downloadEnabled && playEnabled"
          >当前：不能下载，但可以在线互动使用。</span
        >
        <span v-else-if="downloadEnabled && !playEnabled"
          >当前：可以下载，但不能在线互动使用。</span
        >
        <span v-else>当前：下载与在线互动均已关闭，用户只能浏览、搜索、收藏。</span>
      </div>

      <!-- 说明 -->
      <div class="mt-8 border-t border-neutral-100 pt-5">
        <h3 class="text-sm font-bold text-neutral-800 mb-3">说明</h3>
        <ul class="text-sm text-neutral-600 space-y-2 list-disc pl-5">
          <li>关闭后前端按钮会置灰并真正不可点击，后端同时拦截，直接调接口也会被拒绝。</li>
          <li>
            已下载过的用户在关闭期间也无法再次获取文件；重新开启后恢复原样，不额外扣创值。
          </li>
          <li>两个开关只影响「获取文件」，不影响浏览、搜索、收藏、评论、发布与创值。</li>
          <li>保存后用户刷新页面即可生效，无需重启服务。</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";

export default {
  name: "DownloadSetting",
  data() {
    return {
      downloadEnabled: true,
      playEnabled: true,
      saving: false,
      tip: "",
      tipOk: true,
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const res = await this.$axios({ url: "point/config", method: "GET" });
        if (res.code === 200 && res.data) {
          this.downloadEnabled = res.data.downloadEnabled !== false;
          this.playEnabled = res.data.playEnabled !== false;
        }
      } catch (e) {
        console.error("加载权限配置失败:", e);
      }
    },
    async save() {
      this.saving = true;
      this.tip = "";
      try {
        const res = await this.$axios({
          url: "point/admin/config",
          method: "POST",
          data: {
            data: await this.$aesEncrypt({
              downloadEnabled: this.downloadEnabled ? 1 : 0,
              playEnabled: this.playEnabled ? 1 : 0,
            }),
          },
        });
        if (res.code === 200) {
          const parts = [];
          parts.push(this.downloadEnabled ? "下载已开启" : "下载已关闭");
          parts.push(this.playEnabled ? "在线互动已开启" : "在线互动已关闭");
          this.tip = "已保存：" + parts.join(" · ");
          this.tipOk = true;
          ElMessage({ message: this.tip, type: "success" });
        } else {
          this.tip = res.msg || "保存失败";
          this.tipOk = false;
        }
      } catch (e) {
        this.tip = "保存失败";
        this.tipOk = false;
      }
      this.saving = false;
    },
  },
};
</script>
